/**
 * Serviço de Integração VidSell ↔ Grana em Dia
 * Validação de brinde e sincronização contínua de status
 */
import { supabase } from '@/lib/supabase';
import type { UserProfile } from '@/types/user';

const VIDSELL_API_URL = import.meta.env.VITE_VIDSELL_API_URL || 'https://vidsell.infinityai.com.br';

export interface VidSellBonusResponse {
  ok: boolean;
  active: boolean;
  status: 'active' | 'expired' | 'not_eligible' | 'not_activated';
  plan: 'premium';
  valid_until?: string | null;
  message?: string;
}

/**
 * Consulta o status do código de brinde no VidSell
 */
export async function queryVidSellBonus(code: string): Promise<VidSellBonusResponse> {
  const cleanCode = String(code || '').trim().toUpperCase();
  if (!cleanCode) {
    return {
      ok: false,
      active: false,
      status: 'not_eligible',
      plan: 'premium',
      message: 'Código não informado.',
    };
  }

  try {
    const res = await fetch(`${VIDSELL_API_URL}/api/bonus?code=${encodeURIComponent(cleanCode)}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      return {
        ok: false,
        active: false,
        status: 'not_eligible',
        plan: 'premium',
        message: `Falha ao consultar VidSell (${res.status}): ${errText || 'Erro no servidor'}`,
      };
    }

    const data = await res.json();
    return data as VidSellBonusResponse;
  } catch (err) {
    console.error('[VidSell] Erro na consulta de bônus:', err);
    return {
      ok: false,
      active: false,
      status: 'not_eligible',
      plan: 'premium',
      message: 'Não foi possível conectar ao servidor do VidSell. Verifique sua conexão.',
    };
  }
}

/**
 * Ativa o brinde VidSell no perfil do usuário
 */
export async function claimVidSellBonus(userId: string, code: string): Promise<{ success: boolean; message: string }> {
  const cleanCode = String(code || '').trim().toUpperCase();
  const result = await queryVidSellBonus(cleanCode);

  if (!result.active || !result.valid_until) {
    return {
      success: false,
      message: result.message || 'Código não elegível ou inativo no VidSell.',
    };
  }

  try {
    const { error } = await supabase
      .from('profiles')
      .update({
        subscription_plan: 'premium',
        subscription_status: 'active',
        subscription_ends_at: result.valid_until,
        vidsell_claim_code: cleanCode,
        vidsell_last_sync: new Date().toISOString(),
      })
      .eq('user_id', userId);

    if (error) {
      console.error('[VidSell] Erro ao salvar perfil:', error);
      return {
        success: false,
        message: 'Código validado no VidSell, mas houve um erro ao atualizar seu perfil.',
      };
    }

    return {
      success: true,
      message: `Plano Premium com IA ativado com sucesso! Válido até ${new Date(result.valid_until).toLocaleDateString('pt-BR')}.`,
    };
  } catch (err) {
    console.error('[VidSell] Exceção ao atualizar perfil:', err);
    return {
      success: false,
      message: 'Erro inesperado ao salvar ativação no banco de dados.',
    };
  }
}

/**
 * Sincroniza em background o status da assinatura VidSell
 * Chamado automaticamente na inicialização do app quando o usuário tem código vinculado
 */
export async function syncVidSellSubscription(userProfile: UserProfile): Promise<boolean> {
  if (!userProfile.vidsell_claim_code) return false;

  const lastSync = userProfile.vidsell_last_sync ? Date.parse(userProfile.vidsell_last_sync) : 0;
  const twelveHours = 12 * 60 * 60 * 1000;
  const isExpiringSoon = userProfile.subscription_ends_at 
    ? (Date.parse(userProfile.subscription_ends_at) - Date.now() < 3 * 24 * 60 * 60 * 1000)
    : false;

  // Evita requisições excessivas: sincroniza a cada 12h ou se estiver perto de vencer
  if (Date.now() - lastSync < twelveHours && !isExpiringSoon) {
    return false;
  }

  const result = await queryVidSellBonus(userProfile.vidsell_claim_code);

  if (result.active && result.valid_until) {
    // Se a validade no VidSell foi estendida (renovação mensal)
    if (result.valid_until !== userProfile.subscription_ends_at || userProfile.subscription_status !== 'active') {
      await supabase
        .from('profiles')
        .update({
          subscription_plan: 'premium',
          subscription_status: 'active',
          subscription_ends_at: result.valid_until,
          vidsell_last_sync: new Date().toISOString(),
        })
        .eq('user_id', userProfile.user_id);
      return true;
    }
  } else if (!result.active) {
    // Se a assinatura do VidSell foi cancelada/expirou
    if (userProfile.subscription_status === 'active') {
      await supabase
        .from('profiles')
        .update({
          subscription_status: 'canceled',
          vidsell_last_sync: new Date().toISOString(),
        })
        .eq('user_id', userProfile.user_id);
      return true;
    }
  }

  // Apenas atualiza a data do sync
  await supabase
    .from('profiles')
    .update({ vidsell_last_sync: new Date().toISOString() })
    .eq('user_id', userProfile.user_id);

  return false;
}
